/**
 * Sysadmin Audit module — TanStack Query hooks (Wave 3 §10.2 #7).
 *
 * Wraps:
 *   GET  /admin/platform/audit/stream            → useAuditStream
 *   GET  /admin/platform/audit/sysadmin-actions  → useSysadminActions
 *   POST /admin/platform/audit-chain/verify      → useChainVerify
 *                                                  (#1129 — was GET;
 *                                                  verify mutates state)
 *
 * The two list endpoints are read-only sysadmin reads; chain verify
 * now mutates (writes PLATFORM_AUDIT_VERIFIED) so it goes through
 * POST with fresh-TOTP step-up enforced server-side.
 *
 * Per spec §8.6 the audit chain is continuous; the UI uses
 * cursor-based "Load more" with a virtualized list rather than
 * page-numbered pagination. The hooks expose `nextCursor` so the
 * caller can stitch.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api-client';
import { createSysadminStepUpMutation } from './use-sysadmin-query';

// ─── Stream ─────────────────────────────────────────────────────────────────

export interface AuditStreamRow {
  id: string;
  organizationId: string | null;
  organizationName: string | null;
  userId: string | null;
  userEmail: string | null;
  action: string;
  category: string | null;
  outcome: string | null;
  resourceType: string;
  resourceId: string | null;
  ipAddress: string | null;
  createdAt: string;
  cursor: string;
  // #608 — chain provenance columns. Rows written via the canonical
  // appendAdminAuditEvent path carry recordHash + previousHash +
  // sequenceNumber and have `inChain: true`. Legacy direct INSERTs
  // (auth LOGIN_*, older codepaths) come through with NULL hashes and
  // `inChain: false` — the UI must label those rows as "out-of-chain"
  // so an auditor doesn't mistake legacy authentication rows for
  // tamper-evident chain proof.
  recordHash: string | null;
  previousHash: string | null;
  sequenceNumber: string | null;
  inChain: boolean;
}

export interface AuditStreamResponse {
  items: AuditStreamRow[];
  nextCursor: string | null;
}

export interface AuditStreamFilters {
  tenantId?: string;
  action?: string;
  outcome?: 'success' | 'failure';
  category?: string;
  cursor?: string;
  limit?: number;
}

export function useAuditStream(filters: AuditStreamFilters = {}) {
  const params = new URLSearchParams();
  if (filters.tenantId) params.set('tenantId', filters.tenantId);
  if (filters.action) params.set('action', filters.action);
  if (filters.outcome) params.set('outcome', filters.outcome);
  if (filters.category) params.set('category', filters.category);
  if (filters.cursor) params.set('cursor', filters.cursor);
  if (filters.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();

  return useQuery<AuditStreamResponse, Error>({
    queryKey: ['sysadmin-audit-stream', filters],
    queryFn: () =>
      apiClient.get<AuditStreamResponse>(
        `/admin/platform/audit/stream${qs ? `?${qs}` : ''}`,
      ),
  });
}

// ─── Sysadmin Actions ───────────────────────────────────────────────────────

export interface SysadminActionRow {
  id: string;
  sysadminId: string;
  sysadminEmail: string;
  action: string;
  category: string;
  outcome: string;
  targetTenantId: string | null;
  targetTenantName: string | null;
  targetResourceType: string | null;
  targetResourceId: string | null;
  ipAddress: string | null;
  /**
   * #1125 — forensic context for sysadmin actions. The API extracts
   * these from `platform_audit_logs.details` so the UI can render
   * accountability metadata without raw JSON drill-down. `details`
   * carries any remaining (redacted) structured payload; sensitive
   * keys like password / token / secret / key / otp are stripped on
   * the server.
   */
  userAgent: string | null;
  reason: string | null;
  sessionId: string | null;
  requestId: string | null;
  stepUpEvidence: string | null;
  details: Record<string, unknown> | null;
  createdAt: string;
  cursor: string;
}

export interface SysadminActionsResponse {
  items: SysadminActionRow[];
  nextCursor: string | null;
}

export interface SysadminActionsFilters {
  sysadminId?: string;
  action?: string;
  outcome?: 'success' | 'failure';
  targetTenantId?: string;
  cursor?: string;
  limit?: number;
}

export function useSysadminActions(filters: SysadminActionsFilters = {}) {
  const params = new URLSearchParams();
  if (filters.sysadminId) params.set('sysadminId', filters.sysadminId);
  if (filters.action) params.set('action', filters.action);
  if (filters.outcome) params.set('outcome', filters.outcome);
  if (filters.targetTenantId) params.set('targetTenantId', filters.targetTenantId);
  if (filters.cursor) params.set('cursor', filters.cursor);
  if (filters.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();

  return useQuery<SysadminActionsResponse, Error>({
    queryKey: ['sysadmin-audit-actions', filters],
    queryFn: () =>
      apiClient.get<SysadminActionsResponse>(
        `/admin/platform/audit/sysadmin-actions${qs ? `?${qs}` : ''}`,
      ),
  });
}

// ─── Chain integrity verify (W1a-26 endpoint) ───────────────────────────────

export type ChainScope = 'platform' | 'tenant';

export interface ChainMismatch {
  id: string;
  reason: string;
}

export interface ChainVerifyResponse {
  scope: ChainScope;
  tenantId: string | null;
  ok: boolean;
  rowsVerified: number;
  firstMismatch: ChainMismatch | null;
}

export interface ChainVerifyInput {
  scope: ChainScope;
  tenantId?: string;
  from?: string;
  to?: string;
}

/**
 * Chain-integrity verify. This is a tier-1 destructive mutation (it writes a
 * PLATFORM_AUDIT_VERIFIED row + requires a fresh-TOTP step-up), so it MUST go
 * through `createSysadminStepUpMutation` to attach the `X-Step-Up-Reason`
 * header — the prior `useQuery` + bare `apiClient.post` sent no reason and
 * deterministically 400'd (REASON_REQUIRED), so "Run verification" never worked
 * (platform audit 2026-06-10). The caller supplies the reason via the
 * step-up-reason dialog: `verify.mutate({ input, stepUpReason })`.
 */
export const useChainVerify = createSysadminStepUpMutation<
  ChainVerifyInput,
  ChainVerifyResponse
>({
  url: '/admin/platform/audit-chain/verify',
  method: 'POST',
});
